import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import SortableTable from '../SortableTable';
import { RiStore2Line } from 'react-icons/ri';
import StatTile from '../ui/StatTile';
import { API_BASE_URL } from '../../config';
import { useSelectStyles } from '../../utils/selectStyles';
import { formatDate } from '../../utils/dateUtils';
import { getDNColorName } from '../../utils/dnUtils';
import Toggle from '../ui/Toggle';
import { useDN } from '../../contexts/DNContext';
import { _t } from '../../utils/i18n';

const DNInterventionPoint = ({ selectedProject, fetchWithAuth, selectedShopSigns, selectedCategories, obfuscate }) => {
  const selectStyles = useSelectStyles();
  const [interventionPoints, setInterventionPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [pointData, setPointData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOutOfRange, setShowOutOfRange] = useState(false);
  let { startDate, endDate } = useDN();

  startDate = new Date(startDate).toISOString().split('T')[0];
  endDate = new Date(endDate).toISOString().split('T')[0];

  // Récupérer la liste des points d'intervention
  useEffect(() => {
    const fetchInterventionPoints = async () => {
      if (!selectedProject) return;

      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/intervention-points?startDate=${startDate}&endDate=${endDate}`
        );

        const data = await response.json();

        const options = data.map(point => ({
          value: point.INTERVENTION_POINT,
          label: point.INTERVENTION_POINT
        })).sort((b, a) => b.label.localeCompare(a.label));

        setInterventionPoints(options);
      } catch (error) {
        console.error('Erreur lors de la récupération des points d\'intervention:', error);
      }
    };

    fetchInterventionPoints();
  }, [selectedProject, fetchWithAuth, startDate, endDate]);

  // Récupérer les données pour le point d'intervention sélectionné
  useEffect(() => {
    const fetchPointData = async () => {
      if (!selectedPoint) return;

      setLoading(true);

      try {
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/intervention-point?startDate=${startDate}&endDate=${endDate}&interventionPoint=${encodeURIComponent(selectedPoint.value)}`
        );

        const data = await response.json();

        setPointData(data);
      } catch (error) {
        console.error('Erreur lors de la récupération des données du point:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPointData();
  }, [selectedPoint, selectedProject, fetchWithAuth, startDate, endDate]);

  // Ajout d'un useMemo pour filtrer les données en fonction des shop signs et catégories
  const filteredPointData = useMemo(() => {
    if (!pointData || pointData.length === 0) return [];

    let filtered = [...pointData];

    // Filtre par enseigne si nécessaire
    if (selectedShopSigns && selectedShopSigns.length > 0) {
      const shopSignValues = selectedShopSigns.map(sign => sign.value);
      filtered = filtered.filter(item => shopSignValues.includes(item.SHOP_SIGN));
    }

    // Filtre par catégorie si nécessaire
    if (selectedCategories && selectedCategories.length > 0) {
      const categoryValues = selectedCategories.map(cat => cat.value);
      filtered = filtered.filter(item => categoryValues.includes(item.CATEGORY));
    }

    return filtered;
  }, [pointData, selectedShopSigns, selectedCategories]);

  // Modifier processedData pour filtrer les produits hors référencement selon l'état du switch
  const processedData = useMemo(() => {
    if (!filteredPointData.length) return [];

    // Grouper par produit
    const productGroups = {};

    filteredPointData.forEach(item => {
      const product = item.PRODUCT;
      if (!productGroups[product]) {
        productGroups[product] = [];
      }
      productGroups[product].push(item);
    });

    // Créer les données traitées pour tous les produits
    let processed = Object.keys(productGroups)
      .sort((a, b) => a.localeCompare(b))
      .map(product => {
        const items = productGroups[product];
        items.sort((a, b) => new Date(b.DATE) - new Date(a.DATE));

        const latest = items[0];
        const previous = items.length > 1 ? items[1] : null;

        return {
          product,
          category: latest.CATEGORY,
          latest: {
            date: latest.DATE,
            isPresent: latest.IS_PRESENT,
            reason: latest.REASON,
            reasonCategory: latest.REASON_CATEGORY,
            userName: latest.USER_NAME
          },
          previous: previous ? {
            date: previous.DATE,
            isPresent: previous.IS_PRESENT,
            reason: previous.REASON,
            reasonCategory: previous.REASON_CATEGORY,
            userName: previous.USER_NAME
          } : null
        };
      });

    // Appliquer le filtre seulement pour l'affichage si nécessaire
    if (!showOutOfRange) {
      processed = processed.filter(item =>
        item.latest.isPresent === true ||
        (item.latest.isPresent === false && item.latest.reasonCategory !== 'Not in Store Range')
      );
    }

    return processed;
  }, [filteredPointData, showOutOfRange]);

  // Calculer les statistiques sur les données filtrées
  const stats = useMemo(() => {
    if (!filteredPointData.length) return { total: 0, present: 0, absent: 0, outOfRange: 0, dnRate: 0 };

    // Créer un jeu de données non filtrées pour calculer les statistiques complètes
    const allProductGroups = {};

    filteredPointData.forEach(item => {
      const product = item.PRODUCT;
      if (!allProductGroups[product]) {
        allProductGroups[product] = [];
      }
      allProductGroups[product].push(item);
    });

    // Traiter toutes les données pour les statistiques
    const allProducts = Object.keys(allProductGroups).map(product => {
      const items = allProductGroups[product];
      items.sort((a, b) => new Date(b.DATE) - new Date(a.DATE));
      return {
        product,
        latest: {
          isPresent: items[0].IS_PRESENT,
          reasonCategory: items[0].REASON_CATEGORY
        }
      };
    });

    const total = allProducts.length;
    const present = allProducts.filter(item => item.latest.isPresent).length;
    const outOfRange = allProducts.filter(item => !item.latest.isPresent && item.latest.reasonCategory === 'Not in Store Range').length;
    const absent = allProducts.filter(item => !item.latest.isPresent && item.latest.reasonCategory !== 'Not in Store Range').length;

    // Calculer le taux de DN en excluant les produits hors référencement
    const relevantCount = total - outOfRange;
    const dnRate = relevantCount > 0 ? (present / relevantCount * 100).toFixed(1) : '0.0';

    return { total, present, absent, outOfRange, dnRate };
  }, [filteredPointData]);

  const columns = [
    {
      key: 'product',
      label: _t('Product'),
      sortable: true,
      render: (item) => {
        return obfuscate ? `${item.product.slice(0, 2)}${'*'.repeat(Math.max(0, item.product.length - 2))}` : item.product;
      }
    },
    {
      key: 'category',
      label: _t('Category'),
      sortable: true
    },
    {
      key: 'latestStatus',
      label: _t('Latest report'),
      rawValue: (item) => {
        const status = item.latest;
        if (!status.isPresent && status.reasonCategory !== 'Not in Store Range') return 0;
        if (status.isPresent) return 1;
        return 2;
      },
      sortFunction: (a, b, direction) => {
        const getStatusValue = (item) => {
          const status = item.latest;
          if (!status.isPresent && status.reasonCategory !== 'Not in Store Range') return 0;
          if (status.isPresent) return 1;
          return 2;
        };

        const aValue = getStatusValue(a);
        const bValue = getStatusValue(b);

        return direction === 'ascending' ? aValue - bValue : bValue - aValue;
      },
      render: (item) => {
        const status = item.latest;
        return (
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className={`inline-block w-3 h-3 rounded-full mr-2 ${status.isPresent ? 'bg-green-500' : status.reasonCategory === 'Not in Store Range' ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
              <span className="font-medium">{status.isPresent ? _t('Present') : status.reasonCategory === 'Not in Store Range' ? _t('Not in Store Range') : _t('Absent')}</span>
              {!status.isPresent && status.reasonCategory !== 'Not in Store Range' && (
                <span className="ml-2 text-sm text-gray-500">({status.reason || _t('Reason not specified')})</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatDate(status.date)} - {status.userName}
            </div>
          </div>
        );
      }
    },
    {
      key: 'previousStatus',
      label: _t('Previous report'),
      render: (item) => {
        const status = item.previous;
        if (!status) return <span className="text-gray-400">Pas de relevé antérieur</span>;

        return (
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className={`inline-block w-3 h-3 rounded-full mr-2 ${status.isPresent ? 'bg-green-500' : status.reasonCategory === 'Not in Store Range' ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
              <span className="font-medium">{status.isPresent ? 'Présent' : status.reasonCategory === 'Not in Store Range' ? 'Hors référencement' : 'Absent'}</span>
              {!status.isPresent && status.reasonCategory !== 'Not in Store Range' && (
                <span className="ml-2 text-sm text-gray-500">({status.reason || 'Raison non précisée'})</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {formatDate(status.date)} - {status.userName}
            </div>
          </div>
        );
      },
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <div className="w-full md:flex-1">
          <Select
            value={selectedPoint}
            onChange={setSelectedPoint}
            options={interventionPoints}
            isSearchable
            placeholder={_t('Select a store...')}
            className="w-full"
            classNamePrefix="select"
            styles={selectStyles}
          />
        </div>

        <Toggle
          checked={showOutOfRange}
          onChange={() => setShowOutOfRange(!showOutOfRange)}
          label={_t('Display products not in store range')}
          labelPosition="left"
        />
      </div>

      {/* Date du dernier relevé */}
      {filteredPointData.length > 0 && (
        <div className="flex justify-end mb-2">
          <span className="text-xs text-gray-500 italic">
            {_t('Latest report')} : {formatDate(filteredPointData[0].DATE)}
          </span>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {selectedPoint && !loading && processedData.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatTile
              label={_t('Present products')}
              value={stats.present.toString()}
              icon={<RiStore2Line />}
              color="green"
            />
            <StatTile
              label={_t('Absent products')}
              value={stats.absent.toString()}
              icon={<RiStore2Line />}
              color="red"
            />
            <StatTile
              label={_t('Not in Store Range')}
              value={stats.outOfRange.toString()}
              icon={<RiStore2Line />}
              color="amber"
            />
            <StatTile
              label={_t('DN rate (%)')}
              value={`${stats.dnRate}%`}
              icon={<RiStore2Line />}
              color={getDNColorName(stats.dnRate)}
            />
          </div>

          <SortableTable
            data={processedData}
            columns={columns}
            initialSortBy={{ key: 'product', direction: 'ascending' }}
            emptyMessage={_t('No product found for this intervention point')}
            exportable={true}
            exportFilename={`DN_${selectedPoint.value.replace(/\s+/g, '_')}.csv`}
          />
        </>
      )}

      {selectedPoint && !loading && processedData.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-lg">
          <p className="text-yellow-800 dark:text-yellow-200">
            {!showOutOfRange
              ? _t('No product found for this intervention point (try to activate the display of products not in store range)')
              : _t('No data available for this intervention point.')
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default DNInterventionPoint;
